import { Button } from '@heroui/react';
import { isVideoUploadInterrupted, mapLessonVideo } from '@/features/StepVideoUploads';
import type { ContentStep } from '@/entities/Courses';
import { PrivateFileLink } from '@/shared/ui/PrivateFileLink';
import { Svg } from '@/shared/ui/Svg';
import DownloadIcon from '@/shared/assets/icons/download.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import { LessonVideo } from '../LessonVideo';

interface LessonStepsProps {
  steps: ContentStep[];
  onPreviewScorm?: (packageId: number) => void;
  isScormPreviewLoading?: boolean;
}

const STEP_TYPE_LABEL: Record<ContentStep['type'], string> = {
  general: 'General',
  video: 'Video',
  scorm: 'SCORM',
};

const stepTitle = (step: ContentStep) =>
  step.title || (step.type === 'video' ? 'Video' : `Step ${step.order}`);

/**
 * A video or SCORM package that is not ready: why, and the placeholder students get instead
 * (the same text as in the LMS lesson step)
 */
const StepUnavailable = ({ reason, studentText }: { reason: string; studentText: string }) => (
  <div className="flex flex-col gap-1 rounded-[12px] border border-grey-border p-6 text-sm">
    <span className="font-medium text-black">{reason}</span>
    <span className="text-grey-secondary">
      Until then, students see “{studentText}” in this step.
    </span>
  </div>
);

const getVideoUnavailableReason = (video: ReturnType<typeof mapLessonVideo>) => {
  if (!video) return 'Video is not uploaded';
  if (video.status === 'error') return 'Video failed to process';
  if (isVideoUploadInterrupted(video)) return 'Video upload has not finished';
  return 'Video is being processed. It will appear in the session once it is ready';
};

const getScormUnavailableReason = (scorm: ContentStep['scorm']) => {
  if (!scorm) return 'SCORM package is not uploaded';
  if (scorm.processingStatus === 'failed') return 'SCORM package failed to process';
  if (scorm.processingStatus === 'uploading') return 'SCORM package upload has not finished';
  return 'SCORM package is being processed. It will appear in the session once it is ready';
};

const StepVideo = ({ step }: { step: ContentStep }) => {
  const video = mapLessonVideo(step.video);

  // Students get only ready videos
  if (!video || video.status !== 'ready') {
    return (
      <StepUnavailable
        reason={getVideoUnavailableReason(video)}
        studentText="Video is not available yet"
      />
    );
  }

  if (video.embedUrl) {
    return (
      <LessonVideo
        video={{ embedUrl: video.embedUrl, thumbnailUrl: video.thumbnailUrl, name: stepTitle(step) }}
      />
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-black">
      <video controls className="aspect-video w-full" src={video.url} />
    </div>
  );
};

const StepScorm = ({
  step,
  onPreviewScorm,
  isScormPreviewLoading,
}: { step: ContentStep } & Omit<LessonStepsProps, 'steps'>) => {
  const scorm = step.scorm;
  const isReady = scorm?.processingStatus === 'ready' && scorm.isValid;

  if (!scorm || !isReady) {
    return (
      <StepUnavailable
        reason={getScormUnavailableReason(scorm)}
        studentText="Interactive content is not available yet"
      />
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 rounded-[12px] bg-[#F4F4F5] px-4 py-3">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-black">{scorm.title}</span>
        <span className="text-xs text-grey-secondary">
          {scorm.hasAssessment && scorm.passingScore
            ? `Passed when score ≥ ${scorm.passingScore}%`
            : 'Completed when finished'}
        </span>
      </div>
      {onPreviewScorm && (
        <Button
          radius="full"
          className="bg-[#211D55] text-white"
          isLoading={isScormPreviewLoading}
          onPress={() => onPreviewScorm(scorm.packageId)}
          endContent={<Svg Icon={OpenedEyeIcon} width={18} height={18} />}
        >
          Preview SCORM
        </Button>
      )}
    </div>
  );
};

export const LessonSteps = ({ steps, onPreviewScorm, isScormPreviewLoading }: LessonStepsProps) => (
  <div className="flex flex-col gap-4">
    {steps.map((step) => (
      <section key={step.id} className="flex flex-col gap-4 rounded-[20px] bg-white p-6">
        <div className="flex flex-col gap-1">
          <p className="text-xs text-grey-secondary">
            Step {step.order} · {STEP_TYPE_LABEL[step.type]}
          </p>
          <h3 className="text-xl font-medium text-black">{stepTitle(step)}</h3>
          {step.description && <p className="text-sm text-grey-secondary">{step.description}</p>}
        </div>

        {step.content && (
          <p className="whitespace-pre-line text-sm text-black-headline">{step.content}</p>
        )}

        {step.type === 'video' && <StepVideo step={step} />}

        {step.type === 'scorm' && (
          <StepScorm
            step={step}
            onPreviewScorm={onPreviewScorm}
            isScormPreviewLoading={isScormPreviewLoading}
          />
        )}

        {step.attachments.length > 0 && (
          <div className="flex w-full max-w-[480px] flex-col gap-2">
            {step.attachments.map((file) => (
              <PrivateFileLink
                key={file.key}
                file={file}
                action="download"
                className="flex items-center justify-between rounded-[10px] bg-[#F4F4F5] px-4 py-3"
              >
                <span className="truncate text-sm text-black">{file.name}</span>
                <Svg Icon={DownloadIcon} className="text-[#211D55] shrink-0" />
              </PrivateFileLink>
            ))}
          </div>
        )}
      </section>
    ))}
  </div>
);
